import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { backendService } from "../services/backend.service";
import { uiVisibleErrorKey } from "../utils/errors";
import type { MyJobNotification, MyJobNotificationsResponse, SetMyJobNotificationRequest } from "../utils/jobNotificationTypes";

export const MY_JOB_NOTIFICATIONS_QUERY_KEY = ["my-job-notifications"] as const;

/** The backend's error when the tapped job was taken off the employee since the screen loaded. */
export const JOB_NOT_HELD_ERROR_KEY = "jobNotifications.job.not.held";

/** A failed save. `messageKey` is set only when the backend's own message replaces the generic one. */
export type JobNotificationSaveError = { nurseId: number; jobId: string; messageKey?: typeof JOB_NOT_HELD_ERROR_KEY };

/** A failed save and the tap it came from, so an older tap's failure can't replace a newer one's. */
type TrackedSaveError = { error: JobNotificationSaveError; tap: number };

/** The job's name rides along for analytics only and is not sent to the server. */
type SetJobSilentVariables = SetMyJobNotificationRequest & { jobName: string };

const jobKey = (nurseId: number, jobId: string) => `${nurseId}:${jobId}`;

/** Replaces one job on one record, leaving every other record and job as it is. */
const withJob = (
	current: MyJobNotificationsResponse | undefined,
	nurseId: number,
	jobId: string,
	replace: (job: MyJobNotification) => MyJobNotification,
): MyJobNotificationsResponse | undefined =>
	current && {
		facilities: current.facilities.map((facility) =>
			facility.nurseId === nurseId ? { ...facility, jobs: facility.jobs.map((job) => (job.jobId === jobId ? replace(job) : job)) } : facility,
		),
	};

/**
 * The signed-in employee's jobs at each facility record, and whether each one notifies about open shifts.
 * A tap saves right away for that facility only, and rolls back if it fails. `saveErrors` lets the screen show each facility's error next to its chips.
 */
export const useMyJobNotifications = () => {
	const queryClient = useQueryClient();
	const { track } = useAnalytics();
	// Kept in state because the mutation only tracks the latest tap, which would hide an earlier failure.
	// One per facility, so a failure at one facility doesn't hide another's. Within a facility the newest tap's failure shows.
	const [trackedErrors, setTrackedErrors] = useState<TrackedSaveError[]>([]);
	const saveErrors = useMemo(() => trackedErrors.map(({ error }) => error), [trackedErrors]);
	// Taps are numbered so a reply to an older tap of a job, arriving after a newer tap of it, is ignored.
	const tapCount = useRef(0);
	const latestTapByJob = useRef(new Map<string, number>());
	const isLatestTap = (nurseId: number, jobId: string, tap: number) => latestTapByJob.current.get(jobKey(nurseId, jobId)) === tap;

	const query = useQuery({
		queryKey: MY_JOB_NOTIFICATIONS_QUERY_KEY,
		queryFn: async () => (await backendService.getMyJobNotifications()).data,
	});

	const mutation = useMutation({
		mutationFn: async ({ nurseId, jobId, isSilent }: SetJobSilentVariables) => (await backendService.setMyJobNotification({ nurseId, jobId, isSilent })).data,
		onMutate: async ({ nurseId, jobId, isSilent }) => {
			track("bedside_job_notification_save_start", { nurse_id: nurseId, job_id: jobId, is_silent: isSilent });
			tapCount.current += 1;
			const tap = tapCount.current;
			latestTapByJob.current.set(jobKey(nurseId, jobId), tap);
			// A removed job can't be tapped again, so its error clears on the next tap of any job.
			setTrackedErrors((failed) => failed.filter(({ error }) => !error.messageKey && !(error.nurseId === nurseId && error.jobId === jobId)));
			await queryClient.cancelQueries({ queryKey: MY_JOB_NOTIFICATIONS_QUERY_KEY });
			const previousEntry = queryClient
				.getQueryData<MyJobNotificationsResponse>(MY_JOB_NOTIFICATIONS_QUERY_KEY)
				?.facilities.find((facility) => facility.nurseId === nurseId)
				?.jobs.find((job) => job.jobId === jobId);
			queryClient.setQueryData<MyJobNotificationsResponse>(MY_JOB_NOTIFICATIONS_QUERY_KEY, (current) =>
				withJob(current, nurseId, jobId, (job) => ({ ...job, isSilent, turnedOffByScheduler: false })),
			);
			return { previousEntry, tap };
		},
		onError: (error, { nurseId, jobId, isSilent }, context) => {
			track("bedside_job_notification_save_error", { nurse_id: nurseId, job_id: jobId, is_silent: isSilent, error_message: error.message });
			// A newer tap of this job owns the chip and any error about it.
			if (context && !isLatestTap(nurseId, jobId, context.tap)) return;
			// Restore only this job, so a save on another chip in the meantime isn't lost.
			// Refetch when there's nothing to restore, or when the job was removed so its chip drops off.
			const previousEntry = context?.previousEntry;
			if (previousEntry) {
				queryClient.setQueryData<MyJobNotificationsResponse>(MY_JOB_NOTIFICATIONS_QUERY_KEY, (current) =>
					withJob(current, nurseId, jobId, () => previousEntry),
				);
			}
			const jobNotHeld = uiVisibleErrorKey(error) === JOB_NOT_HELD_ERROR_KEY;
			if (!previousEntry || jobNotHeld) queryClient.invalidateQueries({ queryKey: MY_JOB_NOTIFICATIONS_QUERY_KEY });
			const saveError: JobNotificationSaveError = jobNotHeld ? { nurseId, jobId, messageKey: JOB_NOT_HELD_ERROR_KEY } : { nurseId, jobId };
			// DEFAULT: with no context the tap's own setup failed, so this failure counts as the newest tap.
			const tap = context?.tap ?? tapCount.current;
			setTrackedErrors((failed) => {
				const shown = failed.find(({ error: other }) => other.nurseId === nurseId);
				// An older tap that fails late doesn't replace the error of a newer one at the same facility.
				if (shown && shown.tap > tap) return failed;
				return [...failed.filter(({ error: other }) => other.nurseId !== nurseId), { error: saveError, tap }];
			});
		},
		onSuccess: (data, { nurseId, jobId, isSilent, jobName }, context) => {
			track("bedside_job_notification_save_success", { nurse_id: nurseId, job_id: jobId, is_silent: isSilent });
			// Tracked before the stale-tap check: an older tap's save still changed what the server stores.
			track(isSilent ? "job_notification_muted" : "job_notification_unmuted", { source: "bedside", nurse_id: nurseId, job_id: jobId, job_name: jobName });
			// A newer tap of this job is still on its way, and its reply decides the chip.
			if (context && !isLatestTap(nurseId, jobId, context.tap)) return;
			// Take only this job from the reply. Saves can finish out of order, and the whole reply would undo a newer tap.
			const saved = data.facilities.find((facility) => facility.nurseId === nurseId)?.jobs.find((job) => job.jobId === jobId);
			if (saved) {
				queryClient.setQueryData<MyJobNotificationsResponse>(MY_JOB_NOTIFICATIONS_QUERY_KEY, (current) => withJob(current, nurseId, jobId, () => saved));
			} else {
				// The reply no longer lists this job, so the screen is out of date.
				queryClient.invalidateQueries({ queryKey: MY_JOB_NOTIFICATIONS_QUERY_KEY });
			}
		},
	});

	return {
		facilities: query.data?.facilities,
		isLoading: query.isLoading,
		isError: query.isError,
		refetch: query.refetch,
		isRefetching: query.isRefetching,
		setJobSilent: mutation.mutateAsync,
		/** The latest failed save at each facility, until that job is tapped again. */
		saveErrors,
	};
};
